/* eslint-disable react/self-closing-comp */
import * as React from 'react'
import { useRouter } from 'next/router'
import makeStyles from '@material-ui/core/styles/makeStyles'
import Alert from '@material-ui/lab/Alert'
import clsx from 'clsx'
import { useApp } from 'containers/App/AppContext'
import EmailIcon from 'components/icons/Email'
import Button from 'components/Button'
import FacebookButton from 'components/FacebookButton'
import GoogleButton from 'components/GoogleButton'
import Link from 'components/Link'
import Typography from 'components/Typography'
import { useTranslations } from 'containers/Translations/TranslationsContext'
import getConfig from 'utils/getConfig'
import { useAuthHandlers } from '../AuthContext'
import AuthSocialButton from './AuthSocialButton'

const { publicRuntimeConfig } = getConfig() || {}

const googleClientId = publicRuntimeConfig.GOOGLE_CLIENT_ID

export const useStyles = makeStyles((theme) => ({
  header: {},
  main: {},
  footer: {},
  alert: { marginTop: theme.spacing(1) },
  button: theme.mixins.inputMargin('dense'),
  googleButtonCustom: {
    '& > div ': {
      width: 120,
      display: 'flex',
      justifyContent: 'center',
    },
  },
}))

function AuthLogin(props) {
  const classes = useStyles(props)
  const {
    onLoginOrRegisterScreenToggle,
    onLoginEmailScreenOpen,
    onLoginWithFacebook,
    onLoginWithGoogle,
    onSocialLoginFailure,
  } = useAuthHandlers()

  const { isNavMenuOpen, onLoginDialogClose } = useApp()

  const router = useRouter()

  const [socialError, setSocialError] = React.useState()
  const t = useTranslations()
  const handleSocialLoginFailure = React.useCallback(
    (error) => {
      const messages = onSocialLoginFailure(error)

      setSocialError(messages)
    },
    [onSocialLoginFailure],
  )

  const handleSocialLogin = React.useCallback(
    async (result, provider) => {
      try {
        if (provider === 'facebook') {
          await onLoginWithFacebook(result)
        } else if (provider === 'google') {
          await onLoginWithGoogle(result)
        }

        if (isNavMenuOpen && router.pathname !== '/checkout') {
          router.push('/account/')
        }
        onLoginDialogClose()
      } catch (error) {
        handleSocialLoginFailure(error)
      }
    },
    [
      onLoginWithFacebook,
      onLoginWithGoogle,
      isNavMenuOpen,
      router,
      onLoginDialogClose,
      handleSocialLoginFailure,
    ],
  )

  return (
    <>
      <div className={classes.header}>
        <Typography component="h1" variant="h5">
          {t('Web.AuthLogin.SignIn')}
        </Typography>
      </div>

      <div className={classes.main}>
        <Button
          className={classes.button}
          onClick={onLoginEmailScreenOpen}
          startIcon={<EmailIcon />}
          variant="outlined"
          fullWidth
        >
          {t('Web.AuthLogin.Email')}
        </Button>

        {publicRuntimeConfig.FACEBOOK_APP_ID && (
          <AuthSocialButton
            component={FacebookButton}
            className={classes.button}
            provider="facebook"
            appId={publicRuntimeConfig.FACEBOOK_APP_ID}
            onSuccess={handleSocialLogin}
            onError={handleSocialLoginFailure}
            fullWidth
          >
            {t('Web.AuthLogin.Facebook')}
          </AuthSocialButton>
        )}

        <AuthSocialButton
          component={GoogleButton}
          className={clsx(classes.button, classes.googleButtonCustom)}
          provider="google"
          appId={googleClientId}
          onSuccess={handleSocialLogin}
          onError={handleSocialLoginFailure}
          id="g_id_signin"
          fullWidth
        >
          {t('Web.AuthLogin.Google')}
        </AuthSocialButton>

        {socialError && (
          <Alert className={classes.alert} severity="error">
            {socialError?.map((error, idx) => (
              <div key={idx}>{error}</div>
            ))}
          </Alert>
        )}
      </div>

      <div className={classes.footer}>
        <Typography>{t('Web.AuthLogin.NoAccount')}</Typography>

        <Link // eslint-disable-line jsx-a11y/anchor-is-valid
          onClick={onLoginOrRegisterScreenToggle}
          component="button"
          type="button"
          underline="always"
          variant="body1"
        >
          {t('Web.AuthLogin.SignUp')}
        </Link>
      </div>
    </>
  )
}

export default AuthLogin
